import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Save, Lock, Plus, Trash2, Building2, X } from "lucide-react";
import { toast } from "sonner";

const blankProduct = () => ({ product_name: "", unit: "Tube", business_cost: 0, notes: "", active: true });

// Build supplier-grouped state from the flat materials list (preserves first-seen order).
function toGroups(list) {
  const order = [];
  const map = {};
  (list || []).forEach((m) => {
    const key = m.supplier || "";
    if (!(key in map)) { map[key] = []; order.push(key); }
    map[key].push({ id: m.id, product_name: m.product_name, unit: m.unit || "Tube", business_cost: m.business_cost ?? 0, notes: m.notes || "", active: m.active !== false });
  });
  return order.map((supplier, gi) => ({ _gid: gi, supplier, items: map[supplier] }));
}

export default function Materials() {
  const [groups, setGroups] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nextGid, setNextGid] = useState(1000);

  const load = () => api.get("/materials").then((r) => { const g = toGroups(r.data); setGroups(g); setNextGid(g.length + 1000); });
  useEffect(() => { load(); }, []);
  if (!groups) return <div className="text-slate-400">Loading materials…</div>;

  const mutate = (fn) => setGroups((gs) => { const s = structuredClone(gs); fn(s); return s; });
  const updateSupplier = (gi, val) => mutate((s) => { s[gi].supplier = val; });
  const addProduct = (gi) => mutate((s) => { s[gi].items.push(blankProduct()); });
  const removeProduct = (gi, pi) => mutate((s) => { s[gi].items.splice(pi, 1); });
  const updateProduct = (gi, pi, key, val) => mutate((s) => { s[gi].items[pi][key] = val; });
  const removeGroup = (gi) => mutate((s) => { s.splice(gi, 1); });
  const addSupplier = () => { setGroups((gs) => [...gs, { _gid: nextGid, supplier: "", items: [blankProduct()] }]); setNextGid((n) => n + 1); };

  const save = async () => {
    const flat = [];
    for (const g of groups) {
      for (const p of g.items) {
        if (!p.product_name.trim()) { toast.error("Every product needs a name"); return; }
        flat.push({ ...(p.id ? { id: p.id } : {}), supplier: g.supplier.trim(), product_name: p.product_name.trim(), unit: p.unit, business_cost: parseFloat(p.business_cost) || 0, notes: p.notes, active: p.active });
      }
    }
    setSaving(true);
    try {
      const r = await api.put("/materials", { materials: flat });
      const g = toGroups(r.data); setGroups(g); setNextGid(g.length + 1000);
      toast.success("Materials saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><Package className="w-8 h-8" />Materials</h1>
          <p className="text-slate-500 mt-1 flex items-center gap-1"><Lock className="w-4 h-4" />Material costs grouped by supplier — admin only. Feeds job material costs & profitability.</p>
        </div>
        <Button data-testid="materials-save-button" onClick={save} disabled={saving} className="bg-navy hover:bg-navy-light"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Materials"}</Button>
      </div>

      <div className="space-y-5">
        {groups.map((g, gi) => (
          <div key={g._gid} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`supplier-group-${gi}`}>
            <div className="flex items-center gap-3 bg-navy/5 border-b border-slate-100 px-5 py-3">
              <Building2 className="w-5 h-5 text-navy shrink-0" />
              <div className="flex-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-400">Supplier</Label>
                <Input value={g.supplier} onChange={(e) => updateSupplier(gi, e.target.value)} placeholder="Supplier name (e.g. Pasco Construction Solutions)" className="h-9 mt-0.5 font-heading font-semibold text-navy bg-white" data-testid={`supplier-name-${gi}`} />
              </div>
              <button onClick={() => removeGroup(gi)} className="p-2 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 shrink-0" title="Remove supplier & its products" data-testid={`remove-supplier-${gi}`}><Trash2 className="w-4 h-4" /></button>
            </div>

            <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
              {g.items.map((p, pi) => (
                <div key={p.id || `${g._gid}-${pi}`} className="border border-slate-100 rounded-xl p-4" data-testid={`product-${gi}-${pi}`}>
                  <div className="flex items-start justify-between gap-2">
                    <Input value={p.product_name} onChange={(e) => updateProduct(gi, pi, "product_name", e.target.value)} placeholder="Product name" className="font-semibold text-navy h-9 flex-1" data-testid={`product-name-${gi}-${pi}`} />
                    <button onClick={() => removeProduct(gi, pi)} className="p-2 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 shrink-0" data-testid={`product-remove-${gi}-${pi}`}><X className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label className="text-xs text-slate-500">Unit</Label>
                      <Input value={p.unit} onChange={(e) => updateProduct(gi, pi, "unit", e.target.value)} placeholder="Tube / Metres / Each" className="h-8 mt-1" data-testid={`product-unit-${gi}-${pi}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Business Cost</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-slate-400">$</span>
                        <Input type="number" step="0.01" value={p.business_cost} onChange={(e) => updateProduct(gi, pi, "business_cost", e.target.value)} className="h-8" data-testid={`product-cost-${gi}-${pi}`} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500">Notes</Label>
                    <Input value={p.notes} onChange={(e) => updateProduct(gi, pi, "notes", e.target.value)} placeholder="Optional" className="h-8 mt-1" data-testid={`product-notes-${gi}-${pi}`} />
                  </div>
                </div>
              ))}
              <button onClick={() => addProduct(gi)} data-testid={`add-product-${gi}`} className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-slate-500 hover:border-navy hover:text-navy transition-colors inline-flex items-center justify-center gap-2 text-sm font-medium min-h-[80px]"><Plus className="w-4 h-4" />Add product</button>
            </div>
          </div>
        ))}
      </div>

      <button onClick={addSupplier} data-testid="add-supplier" className="w-full border-2 border-dashed border-slate-200 rounded-2xl py-4 text-slate-500 hover:border-navy hover:text-navy transition-colors inline-flex items-center justify-center gap-2 font-medium"><Plus className="w-4 h-4" />Add Supplier</button>
    </div>
  );
}
